import { contentCacheService } from './pdfCacheService';
import { offlineManager } from './offlineManager';

/**
 * Mobile-specific offline functionality
 * Handles mobile device limitations and optimizations
 */
export class MobileOfflineManager {
  constructor() {
    this.isMobile = this.detectMobile();
    this.connectionType = this.getConnectionType();
    this.storageQuota = null;
    this.isLowMemory = false;
    
    // Mobile-specific event listeners
    if (this.isMobile) {
      this.setupMobileListeners();
    }
    
    this.checkStorageQuota();
  }

  /**
   * Detect if device is mobile
   */
  detectMobile() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // Check for mobile user agents
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    const isMobileUA = mobileRegex.test(userAgent);
    
    // Check for touch capability and screen size
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = window.innerWidth <= 768;
    
    return isMobileUA || (isTouchDevice && isSmallScreen);
  }

  /**
   * Get connection type for mobile optimization
   */
  getConnectionType() {
    if ('connection' in navigator) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      return {
        effectiveType: connection.effectiveType || 'unknown',
        downlink: connection.downlink || 0,
        rtt: connection.rtt || 0,
        saveData: connection.saveData || false
      };
    }
    return { effectiveType: 'unknown', downlink: 0, rtt: 0, saveData: false };
  }

  /**
   * Setup mobile-specific event listeners
   */
  setupMobileListeners() {
    // Listen for visibility changes (app backgrounding)
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    
    // Listen for page lifecycle events
    window.addEventListener('pagehide', this.handlePageHide.bind(this));
    window.addEventListener('pageshow', this.handlePageShow.bind(this));
    
    // Listen for memory pressure (if available)
    if ('memory' in performance) {
      this.monitorMemoryUsage();
    }
    
    // Listen for connection changes
    if ('connection' in navigator) {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      connection.addEventListener('change', this.handleConnectionChange.bind(this));
    }
  }

  /**
   * Handle app going to background/foreground
   */
  handleVisibilityChange() {
    if (document.hidden) {
      // console.log('App went to background - pausing downloads');
      this.pauseDownloads();
    } else {
      // console.log('App came to foreground - resuming downloads');
      this.resumeDownloads();
    }
  }

  /**
   * Handle page hide (iOS Safari specific)
   */
  handlePageHide(event) {
    // console.log('Page hidden - saving state');
    this.saveDownloadState();
  }

  /**
   * Handle page show (iOS Safari specific)
   */
  handlePageShow(event) {
    // console.log('Page shown - restoring state');
    this.restoreDownloadState();
  }

  /**
   * Handle connection type changes
   */
  handleConnectionChange() {
    this.connectionType = this.getConnectionType();
    // console.log('Connection changed:', this.connectionType);
    
    // Adjust download strategy based on connection
    if (this.connectionType.saveData || this.connectionType.effectiveType === 'slow-2g') {
      // console.log('Slow connection detected - optimizing downloads');
      this.optimizeForSlowConnection();
    }
  }

  /**
   * Monitor memory usage
   */
  monitorMemoryUsage() {
    setInterval(() => {
      if ('memory' in performance) {
        const memory = performance.memory;
        const usedPercent = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
        
        if (usedPercent > 80) {
          // console.warn('High memory usage detected:', usedPercent + '%');
          this.isLowMemory = true;
          this.handleLowMemory();
        } else {
          this.isLowMemory = false;
        }
      }
    }, 30000); // Check every 30 seconds
  }

  /**
   * Handle low memory situations
   */
  handleLowMemory() {
    // console.log('Handling low memory situation');
    
    // Clear blob URL cache to free memory
    contentCacheService.cleanupBlobUrls();
    
    // Pause non-essential downloads
    this.pauseDownloads();
    
    // Force garbage collection if available
    if (window.gc) {
      window.gc();
    }
  }

  /**
   * Check storage quota (mobile devices have limited storage)
   */
  async checkStorageQuota() {
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        this.storageQuota = {
          quota: estimate.quota,
          usage: estimate.usage,
          available: estimate.quota - estimate.usage,
          usagePercent: (estimate.usage / estimate.quota) * 100
        };
        
        // console.log('Storage quota:', this.storageQuota);
        
        // Warn if storage is getting full
        if (this.storageQuota.usagePercent > 80) {
          // console.warn('Storage quota nearly full:', this.storageQuota.usagePercent + '%');
        }
      }
    } catch (error) {
      // console.error('Error checking storage quota:', error);
    }
  }

  /**
   * Mobile-optimized content caching
   */
  async cacheContentMobile(url, contentId, contentName, contentType = 'unknown') {
    try {
      // Check if we have enough storage
      await this.checkStorageQuota();
      if (this.storageQuota && this.storageQuota.usagePercent > 90) {
        throw new Error('Storage quota exceeded');
      }
      
      // Check memory situation
      if (this.isLowMemory) {
        // console.warn('Low memory - deferring download');
        return false;
      }
      
      // Optimize for mobile connection
      if (this.connectionType.saveData) {
        // console.log('Data saver mode - asking user confirmation');
        const userConfirmed = await this.askUserConfirmation(contentName);
        if (!userConfirmed) {
          return false;
        }
      }
      
      // Use smaller chunk sizes for mobile
      return await this.cacheWithChunking(url, contentId, contentName, contentType);
      
    } catch (error) {
      // console.error('Mobile caching error:', error);
      return false;
    }
  }

  /**
   * Cache content with chunking for mobile devices
   */
  async cacheWithChunking(url, contentId, contentName, contentType) {
    try {
      // console.log(`Mobile caching: ${contentName}`);
      
      // For mobile, we'll use the existing cache service but with additional checks
      const success = await contentCacheService.cacheContent(url, contentId, contentName, contentType);
      
      if (success) {
        // Update storage quota after caching
        await this.checkStorageQuota();
      }
      
      return success;
    } catch (error) {
      // console.error('Chunked caching error:', error);
      return false;
    }
  }

  /**
   * Ask user confirmation for downloads on limited connections
   */
  async askUserConfirmation(contentName) {
    return new Promise((resolve) => {
      const confirmed = confirm(
        `You're on a limited data connection. Download "${contentName}" for offline use?`
      );
      resolve(confirmed);
    });
  }

  /**
   * Optimize for slow connections
   */
  optimizeForSlowConnection() {
    // Reduce concurrent downloads
    this.maxConcurrentDownloads = 1;
    
    // Increase timeout for slow connections
    this.downloadTimeout = 60000; // 60 seconds
    
    // console.log('Optimized for slow connection');
  }

  /**
   * Pause downloads (for background/low memory situations)
   */
  pauseDownloads() {
    this.downloadsPaused = true;
    // console.log('Downloads paused');
  }

  /**
   * Resume downloads
   */
  resumeDownloads() {
    this.downloadsPaused = false;
    // console.log('Downloads resumed');
  }

  /**
   * Save download state (for iOS Safari page lifecycle)
   */
  saveDownloadState() {
    try {
      const state = {
        timestamp: Date.now(),
        downloadsPaused: this.downloadsPaused,
        connectionType: this.connectionType
      };
      localStorage.setItem('mobileDownloadState', JSON.stringify(state));
    } catch (error) {
      // console.error('Error saving download state:', error);
    }
  }

  /**
   * Restore download state
   */
  restoreDownloadState() {
    try {
      const stateStr = localStorage.getItem('mobileDownloadState');
      if (stateStr) {
        const state = JSON.parse(stateStr);
        
        // Only restore if state is recent (within 5 minutes)
        if (Date.now() - state.timestamp < 300000) {
          this.downloadsPaused = state.downloadsPaused;
          // console.log('Download state restored');
        }
      }
    } catch (error) {
      // console.error('Error restoring download state:', error);
    }
  }

  /**
   * Get mobile-specific status
   */
  getMobileStatus() {
    return {
      isMobile: this.isMobile,
      connectionType: this.connectionType,
      storageQuota: this.storageQuota,
      isLowMemory: this.isLowMemory,
      downloadsPaused: this.downloadsPaused,
      isOnline: offlineManager.isOnline
    };
  }

  /**
   * Mobile-optimized cache cleanup
   */
  async cleanupForMobile() {
    try {
      // Get current storage usage
      await this.checkStorageQuota();
      
      if (this.storageQuota && this.storageQuota.usagePercent > 85) {
        // console.log('Storage nearly full - cleaning up old cache');
        
        // Get cached items sorted by age
        const cachedItems = await contentCacheService.getCachedItems();
        const oldItems = cachedItems
          .filter(item => Date.now() - item.timestamp > 7 * 24 * 60 * 60 * 1000) // Older than 7 days
          .slice(0, Math.ceil(cachedItems.length * 0.3)); // Remove oldest 30%
        
        // Remove old items
        for (const item of oldItems) {
          await contentCacheService.removeCachedContent(item.contentId, item.contentType);
        }
        
        // console.log(`Cleaned up ${oldItems.length} old cached items`);
        
        // Update storage quota
        await this.checkStorageQuota();
      }
    } catch (error) {
      // console.error('Error during mobile cleanup:', error);
    }
  }

  /**
   * Check if content should be cached based on mobile constraints
   */
  shouldCacheContent(contentSize = 0) {
    // Don't cache if offline
    if (!offlineManager.isOnline) {
      return false;
    }
    
    // Don't cache if downloads are paused
    if (this.downloadsPaused) {
      return false;
    }
    
    // Don't cache if low memory
    if (this.isLowMemory) {
      return false;
    }
    
    // Check storage quota
    if (this.storageQuota) {
      const estimatedUsageAfter = this.storageQuota.usage + contentSize;
      const estimatedPercentAfter = (estimatedUsageAfter / this.storageQuota.quota) * 100;
      
      if (estimatedPercentAfter > 95) {
        return false;
      }
    }
    
    // Check connection type
    if (this.connectionType.saveData && contentSize > 1024 * 1024) { // > 1MB
      return false;
    }
    
    return true;
  }
}

// Create singleton instance
export const mobileOfflineManager = new MobileOfflineManager();

export default mobileOfflineManager;